import { Controller } from "@hotwired/stimulus"
import { debounce } from "lodash"

export default class extends Controller {
  static targets = ["link", "offsetSource"]
  static values = { offset: Number }

  connect() {
    this.links = this.linkTargets.map((link) => {
      const hash = link.getAttribute("href") || ""
      const section = hash.length > 1 ? document.getElementById(hash.slice(1)) : null
      return { link, section }
    })
    this.defaultLink = (this.links.find((entry) => !entry.section) || this.links[0])?.link

    this.pastOffset = new Map()

    if (this.hasOffsetSourceTarget) {
      this.debouncedSetupObserver = debounce(this.setupObserver.bind(this), 150)
      window.addEventListener("resize", this.debouncedSetupObserver)
    }
    this.setupObserver()
  }

  currentOffset() {
    const base = this.hasOffsetValue ? this.offsetValue : 0
    const visible = this.offsetSourceTargets.filter((el) => el.offsetParent !== null)
    const extra = Math.max(0, ...visible.map((el) => el.getBoundingClientRect().height))
    return base + extra
  }

  setupObserver() {
    this.observer?.disconnect()
    this.effectiveOffset = this.currentOffset()
    this.element.style.setProperty("--scroll-spy-offset", `${this.effectiveOffset}px`)
    this.observer = new IntersectionObserver((entries) => this.handleIntersect(entries), {
      rootMargin: `-${this.effectiveOffset}px 0px -${100 - this.effectiveOffset / window.innerHeight * 100}% 0px`
    })
    this.links.forEach(({ section }) => section && this.observer.observe(section))

    this.updateActiveLink()
  }

  disconnect() {
    this.observer?.disconnect()
    if (this.debouncedSetupObserver) {
      window.removeEventListener("resize", this.debouncedSetupObserver)
      this.debouncedSetupObserver.cancel()
    }
  }

  activate(event) {
    this.setActiveLink(event.currentTarget)
  }

  handleIntersect(entries) {
    entries.forEach((entry) => {
      const { top, width, height } = entry.boundingClientRect
      const hidden = width === 0 && height === 0
      // +1px tolerancji — natywny scroll napędzany przez scroll-mt-* ląduje z top
      // ułamek piksela POWYŻEJ offsetu (subpixel rounding), samo "<=" zgubiłoby
      // wtedy właśnie kliknięty link zaraz po nawigacji
      this.pastOffset.set(entry.target, !hidden && top <= this.effectiveOffset + 1)
    })

    this.updateActiveLink()
  }

  updateActiveLink() {
    let current = this.defaultLink

    this.links.forEach(({ link, section }) => {
      if (section && this.pastOffset.get(section)) {
        current = link
      }
    })

    this.setActiveLink(current)
  }

  setActiveLink(activeLink) {
    this.linkTargets.forEach((link) => {
      if (link === activeLink) {
        link.setAttribute("aria-current", "page")
      } else {
        link.removeAttribute("aria-current")
      }
    })
  }

  syncLinks(event) {
    const hiddenGroupIds = new Set(event.detail.hiddenGroupIds)

    this.links.forEach(({ link, section }) => {
      const disabled = !!section && hiddenGroupIds.has(section.id)

      if (disabled) {
        link.setAttribute("aria-disabled", "true")
      } else {
        link.removeAttribute("aria-disabled")
      }
    })
  }
}
